import {type PropsWithChildren, createContext, useCallback, useContext, useMemo, useState} from 'react'

type AddressContextValue = {
  addressIndexes: Array<number>
  currentIndex: number | null
}

type AddressActionsContextValue = {
  addAddress: (index: number) => void
  changeCurrentIndex: (index: number | null) => void
  removeAddress: (index: number) => void
}

type AddressProviderContext = PropsWithChildren<{}>

export const AddressContext = createContext<AddressContextValue | null>(null)
export const AddressActionsContext = createContext<AddressActionsContextValue | null>(null)

export const AddressProvider = ({children}: AddressProviderContext) => {
  const [addressIndexes, setAddressIndexes] = useState<Array<number>>([])
  const [currentIndex, setCurrentIndex] = useState<number | null>(null)

  const addAddress = useCallback((index: number) => {
    setAddressIndexes(prev => [...prev, index].sort())
  }, [])

  const removeAddress = useCallback((index: number) => {
    setAddressIndexes(prev => prev.filter(i => i !== index))
  }, [])

  const changeCurrentIndex = useCallback((index: number | null) => {
    setCurrentIndex(index)
  }, [])

  const value = useMemo<AddressContextValue>(
    () => ({
      addressIndexes,
      currentIndex,
    }),
    [addressIndexes, currentIndex],
  )

  const actions = useMemo<AddressActionsContextValue>(
    () => ({
      addAddress,
      changeCurrentIndex,
      removeAddress,
    }),
    [addAddress, changeCurrentIndex, removeAddress],
  )

  return (
    <AddressContext.Provider value={value}>
      <AddressActionsContext.Provider value={actions}>{children}</AddressActionsContext.Provider>
    </AddressContext.Provider>
  )
}

export const useAddressActionsContext = (): AddressActionsContextValue => {
  const context = useContext(AddressActionsContext)

  if (!context) {
    throw new Error('useAddressActionsContext must be used within an AddressProvider')
  }

  return context
}

export const useAddressContext = (): AddressContextValue => {
  const context = useContext(AddressContext)

  if (!context) {
    throw new Error('useAddressContext must be used within an AddressProvider')
  }

  return context
}
